import { TournamentDetailResponseDto, type RoundInfo, type TournamentInfo } from '@/apis/types/stepinContent';
import { rewardStatus, rewardStatusV2 } from '@/legacy/components/tournament/Status';
import type { useTranslations } from 'next-intl';

export const roundToNumber = {
	FINALS: '2',
	QUARTER_FINALS: '8',
	ROUND_OF_16: '16',
	ROUND_OF_32: '32',
	ROUND_OF_64: '64',
	ROUND_OF_128: '128',
	ROUND_OF_256: '256',
	ROUND_OF_512: '512',
	ROUND_OF_1024: '1024',
	SEMI_FINALS: '4',
	THIRD_PLACE_PLAYOFF: '4',
};

export const beforeRoundNumber = {
	FINALS: '1',
	QUARTER_FINALS: '4',
	ROUND_OF_16: '8',
	ROUND_OF_32: '16',
	ROUND_OF_64: '32',
	ROUND_OF_128: '64',
	ROUND_OF_256: '128',
	ROUND_OF_512: '256',
	ROUND_OF_1024: '512',
	SEMI_FINALS: '2',
	THIRD_PLACE_PLAYOFF: '2',
};

export const thirdPlace = 'THIRD_PLACE_PLAYOFF';
export const numberToRound = {
	'2': 'FINALS',
	'4': 'SEMI_FINALS',
	'8': 'QUARTER_FINALS',
	'16': 'ROUND_OF_16',
	'32': 'ROUND_OF_32',
	'64': 'ROUND_OF_64',
	'128': 'ROUND_OF_128',
	'256': 'ROUND_OF_256',
	'512': 'ROUND_OF_512',
	'1024': 'ROUND_OF_1024',
} as const;

export const roundByNumber = {
	2: ['2'],
	4: ['4', '2'],
	8: ['8', '4', '2'],
	16: ['16', '8', '4', '2'],
	32: ['32', '16', '8', '4', '2'],
	64: ['64', '32', '16', '8', '4', '2'],
	128: ['128', '64', '32', '16', '8', '4', '2'],
	256: ['256', '128', '64', '32', '16', '8', '4', '2'],
	512: ['512', '256', '128', '64', '32', '16', '8', '4', '2'],
	1024: ['1024', '512', '256', '128', '64', '32', '16', '8', '4', '2'],
} as const;

export const prizeMap = [
	[1, 1],
	[2, 2],
	[3, 3],
	[4, 4],
	[5, 8],
	[9, 16],
	[17, 32],
	[33, 64],
	[65, 128],
	[129, 256],
	[257, 512],
	[513, 1024],
];

export const singlePrizeMap = {
	1: '1st',
	2: '2nd',
	3: '3rd',
	4: '4th',
};

export const tierString = (start: number, end: number) => {
	if (start === end && start in singlePrizeMap) {
		return [singlePrizeMap[start as keyof typeof singlePrizeMap]];
	}
	return [`${start}th`, '~', `${end}th`];
};

// THIRD_PLACE_PLAYOFF 처리

export const tournamentNumbers = [2, 4, 8, 16, 32, 64, 128, 256, 512, 1024];

/** Schedule 에 사용되는 타입 매핑 */
export const roundTextMap = {
	FINALS: 'FINALS',
	QUARTER_FINALS: '8',
	ROUND_OF_16: '16',
	ROUND_OF_32: '32',
	ROUND_OF_64: '64',
	ROUND_OF_128: '128',
	ROUND_OF_256: '256',
	ROUND_OF_512: '512',
	ROUND_OF_1024: '1024',
	SEMI_FINALS: 'SEMI',
	THIRD_PLACE_PLAYOFF: '3RD PLACE',
};

// 그냥 _ 지워도 될거 같았는데 아니였고
export const roundMap = {
	FINALS: 'FINALS',
	QUARTER_FINALS: 'ROUND OF 8',
	ROUND_OF_16: 'ROUND OF 16',
	ROUND_OF_32: 'ROUND OF 32',
	ROUND_OF_64: 'ROUND OF 64',
	ROUND_OF_128: 'ROUND OF 128',
	ROUND_OF_256: 'ROUND OF 256',
	ROUND_OF_512: 'ROUND OF 512',
	ROUND_OF_1024: 'ROUND OF 1024',
	SEMI_FINALS: 'SEMI FINALS',
	THIRD_PLACE_PLAYOFF: 'FINALS & 3RD PLACE',
} as const;

export const scheduleRoundMap = {
	FINALS: 'FINALS',
	QUARTER_FINALS: 'R8',
	ROUND_OF_16: 'R16',
	ROUND_OF_32: 'R32',
	ROUND_OF_64: 'R64',
	ROUND_OF_128: 'R128',
	ROUND_OF_256: 'R256',
	ROUND_OF_512: 'R512',
	ROUND_OF_1024: 'R1024',
	SEMI_FINALS: 'SEMI',
	THIRD_PLACE_PLAYOFF: '3RD',
} as const;

export const sortedRounds = (rounds: RoundInfo[]) => {
	const order = Object.keys(roundTextMap);
	return rounds.sort((a, b) => {
		return order.indexOf(a.roundType) - order.indexOf(b.roundType);
	});
};

export const sortedFinalRound = (rounds: RoundInfo[]) => {
	const order = Object.keys(numberToRound);
	return rounds.sort((a, b) => {
		return order.indexOf(a.roundType) - order.indexOf(b.roundType);
	});
};

export const roundNames = Object.keys(roundMap);

export const StickerMap = {
	BEFORE_LIVE: 'Sticker_Status_Live',
	DONE: 'Sticker_Status_Done',
	LIVE: 'Sticker_Status_Live',
	OPEN: 'Sticker_Status_Open',
	SETUP: 'Sticker_Status_Setup',
	UPCOMMING: 'Sticker_Status_Open',
} as const;

export const localeWebToStepin = {
	de: 'de',
	en: 'en',
	es: 'es',
	fr: 'fr',
	id: 'id',
	ja: 'ja',
	ko: 'ko',
	ms: 'ms',
	th: 'th',
	vi: 'vi',
	'zh-cn': 'zh-CN',
	'zh-tw': 'zh-TW',
	mn: 'mn',
} as const;

export const tournamentDTO = (item: TournamentInfo, t: ReturnType<typeof useTranslations<'tournament.reward'>>) => {
	const isDiamond = item.tournament.tournamentType === 'COMMUNITY';
	const isNation = item.tournament.nationFlagImageUrl != null;

	const poster = item.tournament.posterImageUrl;

	const tournamentInfo = item.tournament;

	const isPrivate = item.tournament.isPrivate;
	const status = item.tournament.status;

	/** 현재 진행중인 라운드가 있는 지 여부  */
	const isRound =
		'round' in item.tournament &&
		typeof item.tournament.round === 'object' &&
		item.tournament.round != null &&
		'roundType' in item.tournament.round &&
		item.tournament.round.roundType != null;

	const rewardKey = rewardStatus(isDiamond, tournamentInfo.status);
	// 보상이 캐시거나 , DONE이면 리워드로
	const isPrize = rewardKey === 'prize';
	const diaReward = !isPrize
		? t(rewardKey).replace('$1', String(tournamentInfo[rewardKey]))
		: t(rewardKey).replace('$1', String(tournamentInfo[rewardKey].total ?? 0));

	return {
		diaReward,
		isDiamond,
		isNation,
		isPrivate,
		isPrize,
		isRound,
		poster,
		rewardKey,
		status,
		tournamentInfo,
	} as const;
};

export const tournamentV2DTO = (
	item: TournamentDetailResponseDto,
	t: ReturnType<typeof useTranslations<'tournament.reward'>>
) => {
	const isDiamond = item.tournament.tournamentType === 'CASUAL';
	const isNation = item.tournament.nationFlagImageUrl != null;

	const poster = item.tournament.posterImageUrl;

	const tournamentInfo = item.tournament;

	const isPrivate = item.tournament.isPrivate;
	const status = item.tournament.status;

	/** 현재 진행중인 라운드가 있는 지 여부  */
	const isRound =
		'round' in item.tournament &&
		typeof item.tournament.round === 'object' &&
		item.tournament.round != null &&
		'roundType' in item.tournament.round &&
		item.tournament.round.roundType != null;

	const rewardKey = rewardStatusV2(isDiamond);
	// 보상이 캐시거나 , DONE이면 리워드로

	const isPrize = rewardKey === 'totalCash';

	// 참가자  표시 관련 정책 정해진 거 없음
	//

	const currency = item.tournament.prize?.currency ?? '';
	const totalCash = item.tournament.prize?.totalCash ?? '';

	const diaReward = totalCash + currency;
	return {
		diaReward,
		isDiamond,
		isNation,
		isPrivate,
		isPrize,
		isRound,
		poster,
		rewardKey,
		status,
		tournamentInfo,
	} as const;
};
